import {
  Chart as ChartJS,
  Plugin,
  Chart,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
} from 'chart.js';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import currentTimeZone from '../../../../../config/timeZone';
import { useAppSelector } from '../../../../../redux/hooks';
import BatteryLoader from '../../../../../shared-components/battery-loader/BatteryLoader';
import CustomDateRangePicker from '../../../../../shared-components/custom-date-range/CustomDateRangePicker';
import {
  fillMissingLast24HrsGeneric,
  fillMissingLastNDaysGeneric,
  getBucketFromRange,
} from '../../../../../shared-functions/ChartFunctions';
import { removeEmptyKeys } from '../../../../../shared-functions/DateTime';
import endConsumerV2 from '../../../../../utils/api/end-consumer-v2';

/* ------------------ ChartJS Register ------------------ */
ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Legend);

/* ------------------ Hover Center Line Plugin ------------------ */
const verticalHoverLinePlugin: Plugin<'line'> = {
  id: 'verticalHoverLine',

  /**
   * afterDatasetsDraw
   * @param {any} chart - The chart
   * @returns {any} The result
   */
  afterDatasetsDraw: (chart: Chart<'line'>) => {
    const { tooltip } = chart;

    if (!tooltip || !tooltip.getActiveElements().length) return;

    const { ctx } = chart;
    const activePoint = tooltip.getActiveElements()[0];
    const { x } = activePoint.element;

    const topY = chart.scales.y.top;
    const bottomY = chart.scales.y.bottom;

    const primaryColor =
      getComputedStyle(document.documentElement).getPropertyValue('--primary-color').trim() ||
      '#00A79D';

    ctx.save();

    ctx.beginPath();
    ctx.lineWidth = 1;
    ctx.strokeStyle = primaryColor;
    ctx.lineCap = 'butt'; // straight line (no caps)
    ctx.moveTo(x, topY); // ✅ from top
    ctx.lineTo(x, bottomY); // ✅ to bottom
    ctx.stroke();

    ctx.restore();
  },
};

ChartJS.register(verticalHoverLinePlugin);

interface ChartDataProperties {
  label: string;
  [key: string]: string;
}

interface RevenuePowerSessionsResponse {
  userCustomId: string;
  totalSessions: number;
  totalRevenue: number;
  totalPowerConsumedInKwh: number;
  totalPowerConsumedInKwhPersonal: number;
}
/**
 * Component to display revenue vs power consumed chart
 * @returns {any} The result
 */
export default function TotalConsumptionChart() {
  const { t, i18n } = useTranslation('endConsumer');
  const { selectedEndConsumer, endConsumerType, basicInformation } = useAppSelector(
    (state) => state.endConsumerType,
  );
  const [powerConsumedData, setPowerConsumedData] = useState<ChartDataProperties[]>([]);
  const [revenuePowerSessions, setRevenuePowerSessions] =
    useState<RevenuePowerSessionsResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: dayjs().subtract(24, 'hour').format('YYYY-MM-DD'),
    end: dayjs().format('YYYY-MM-DD'),
  });

  const activeRequestIdReference = React.useRef<number>(0);

  const primaryColor =
    getComputedStyle(document.documentElement).getPropertyValue('--primary-color').trim() ||
    '#00A79D';

  const hasPersonalCharger = Boolean(
    (basicInformation?.personalChargingPoints &&
      basicInformation.personalChargingPoints.length > 0) ||
      (basicInformation?.personalChargingPointCount &&
        basicInformation.personalChargingPointCount > 0),
  );

  const chartData = {
    labels: powerConsumedData.map((d) => d.label),
    datasets: [
      {
        label: t('commercial'),
        data: powerConsumedData.map((d) => Number(d.totalPowerConsumed) || 0),
        borderColor: '#FD4895',
        /**
         * Background
         * @param {any} context - The context
         * @returns {any} The result
         */
        backgroundColor: (context: any) => {
          const { chart } = context;
          const contentContext = chart.ctx;
          const gradient = contentContext.createLinearGradient(0, 0, 0, chart.height);
          gradient.addColorStop(0, 'rgba(247, 144, 9, 0)'); // Alpha 0.4
          gradient.addColorStop(1, 'rgba(237, 237, 237, 0)');
          return gradient;
        },
        fill: 'origin', // Fills to the previous dataset
        tension: 0.4,
        borderWidth: 2,
        
        /**
         * Point radius callback
         * @param {any} context - The context
         * @returns {number} The radius
         */
        pointRadius: (context: any) => (Number(context.raw) > 0 ? 3 : 0),
        pointHoverRadius: 5,
      },
      ...(hasPersonalCharger
        ? [
            {
              label: t('personal', { defaultValue: t('personal') }),
              data: powerConsumedData.map((d) => Number(d.totalPowerConsumedInKwhPersonal) || 0),
              borderColor: primaryColor, // ✅ Primary theme color
              tension: 0.4,
              borderWidth: 2,
              /**
               * Point radius callback
               * @param {any} context - The context
               * @returns {number} The radius
               */
              pointRadius: (context: any) => (Number(context.raw) > 0 ? 3 : 0),
              pointHoverRadius: 5,
            },
          ]
        : []),
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
        position: 'top' as const, // Explicitly cast to 'top' to ensure correct type
        align: 'end' as const,
        labels: {
          usePointStyle: true,
          boxWidth: 10,
          boxHeight: 10,
          borderRadius: 10,
          padding: 15,
        },
        padding: {
          right: 500, // Add 50px padding to the right side of the legend
        },
      },
      tooltip: {
        /**
         * Function to crete tooltip bg color
         * @returns {any} The result
         */
        backgroundColor: () => {
          return 'rgba(255, 255, 255, 1)'; // Default color
        },
        borderColor: primaryColor,
        borderWidth: 1,
        titleColor: '#212529',
        bodyColor: '#212529',
        padding: 10,
        borderRadius: 8,
        cornerRadius: 4,
        displayColors: false,
        callbacks: {
          /**
           * Title callback for chart tooltip
           * @param {any} context - The tooltip context items
           * @returns {any} The result
           */
          title: (context: any) => context?.[0]?.label || t('consumption'),
          /**
           * Consumption values
           * @param {any} context - The context
           * @returns {any} The result
           */
          label: (context: any) => {
            const { label } = context.dataset;
            const value = context.raw ?? 0;

            return `${label}: ${value} ${t('kWh')}`;
          },
        },
      },
    },

    interaction: {
      mode: 'index' as const,
      intersect: false,
    },

    scales: {
      x: {
        grid: {
          display: false,
        },
        border: {
          display: false, // Hides the y-axis line
        },
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          display: true,
        },
        border: {
          display: false,
        },
      },
    },
  };

  /**
   * Fetch power consumed data based on date range
   * @param {string} [start] - The start date (YYYY-MM-DD)
   * @param {string} [end] - The end date (YYYY-MM-DD)
   * @returns {Promise<void>} Promise resolving when data is fetched
   */
  const fetchPowerConsumedReport = async (start?: string, end?: string): Promise<void> => {
    const queryStartDate = start !== undefined ? start : dateRange.start;
    const queryEndDate = end !== undefined ? end : dateRange.end;
    const currentRequestId = activeRequestIdReference.current + 1;
    activeRequestIdReference.current = currentRequestId;

    try {
      setIsLoading(true);

      const query: Record<string, any> = removeEmptyKeys({
        timeZone: currentTimeZone,
        type: endConsumerType,
        startDate: queryStartDate,
        endDate: queryEndDate,
      });

      const response = await endConsumerV2.fetchRevenuePowerSessions(
        selectedEndConsumer?.userCustomId as string,
        query,
      );

      // If user already switched to another range, ignore this response
      if (activeRequestIdReference.current !== currentRequestId) return;

      const commercial = response.data?.results || [];
      const personal = response.data?.resultsPersonal || [];

      // Merge commercial + personal
      const mergedData = commercial.map((c: any) => {
        const match = personal.find(
          (p: any) =>
            (p.date && p.date === c.date) ||
            (p.day && p.day === c.day) ||
            (p.hour !== undefined && p.hour === c.hour) ||
            (p.year && p.year === c.year && p.month === c.month) ||
            (p.label && p.label === c.label),
        );

        return {
          ...c,
          date: c.date || c.day,
          totalPowerConsumedInKwhPersonal: match?.totalPowerConsumed || 0,
        };
      });

      const isMonthlyData =
        (!queryStartDate && !queryEndDate) ||
        (mergedData.length > 0 &&
          (Boolean((mergedData[0] as any).monthName) ||
            Boolean((mergedData[0] as any).year && (mergedData[0] as any).month !== undefined) ||
            ((mergedData[0] as any).date && (mergedData[0] as any).date.length === 7)));

      const { bucket, days } = getBucketFromRange(queryStartDate, queryEndDate);
      if (response.status === 200) {
        if (isMonthlyData || bucket === 'month') {
          const monthlyChartData = mergedData.map((item: any) => {
            const shortMonth = item.monthName
              ? item.monthName.slice(0, 3)
              : dayjs(item.date).format('MMM');
            const displayYear = item.year || dayjs(item.date).format('YYYY');
            return {
              ...item,
              label: `${shortMonth} ${displayYear}`,
            };
          });
          setPowerConsumedData(monthlyChartData);
        } else if (bucket === 'day') {
          setPowerConsumedData(
            fillMissingLastNDaysGeneric(
              mergedData,
              days,
              i18n.language || 'en-US',
              queryStartDate,
              queryEndDate,
            ),
          );
        } else {
          setPowerConsumedData(
            fillMissingLast24HrsGeneric(
              mergedData,
              currentTimeZone,
              i18n.language || 'en-US',
              queryStartDate,
            ),
          );
        }
      }
      setRevenuePowerSessions(response.data);

      setIsLoading(false);
    } catch (error) {
      console.error(error);

      // only stop loading if this request is still the active one
      if (activeRequestIdReference.current === currentRequestId) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (selectedEndConsumer?.userCustomId && endConsumerType) {
      fetchPowerConsumedReport(dateRange.start, dateRange.end);
    }
  }, [selectedEndConsumer?.userCustomId, endConsumerType]);
  return (
    <div className="total-revenue-chart-wrap" dir={i18n.dir()}>
      <div className="total-revenue-chart-header">
        <p className="header-title">{t('activityAndUsage')}</p>
        <div className="label-toggle-container">
          <CustomDateRangePicker
            inputLabel=""
            startDate={dateRange.start}
            endDate={dateRange.end}
            placeholder="DD-MM-YYYY - DD-MM-YYYY"
            onChange={(start, end) => {
              setDateRange({ start, end });
              fetchPowerConsumedReport(start, end);
            }}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="revenue-consumer-loader">
          <BatteryLoader />
        </div>
      ) : (
        <>
          <div className="total-revenue-chart-container">
            <div className="chart-wrapper">
              <Line data={chartData} options={options} />
            </div>
          </div>
          <div className="revenue-footer-container">
            <div className="revenue-footer">
              <div className="color-border commercial-border" />
              <p className="title">
                {t('consumption')} {t('commercial')}
              </p>
              <p className="value">{revenuePowerSessions?.totalPowerConsumedInKwh ?? 0} kWh</p>
            </div>
            {hasPersonalCharger && (
              <div className="revenue-footer">
                <div className="color-border personal-border" />
                <p className="title">
                  {t('consumption')} {t('personal')}
                </p>
                <p className="value">
                  {revenuePowerSessions?.totalPowerConsumedInKwhPersonal ?? 0} kWh
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
